import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// components
import SearchTablePageLayout from '../../../components/momthera/layout/searchTablePage/SearchTablePageLayout';
import CommonModal from '../../../components/modal/CommonModal';

// api
import {
	deletePatientFromMomthera,
	getPatientFromMomthera,
	getPatientPsctcodeFromMomthera,
	postPatientToMomthera,
} from '../../../services/apis/momthera/patient';

const pageInfo = {
	title: '환자 관리',
	search: {
		inputPlaceholder: '이름, 닉네임, 처방코드를 통해 검색할 수 있습니다.',
	},
	list: {
		title: '환자 리스트',
		selectOptionList: [
			{
				key: 'name',
				value: 'name',
				name: '이름',
			},
			{
				key: 'status',
				value: 'status',
				name: '상태',
			},
			{
				key: 'week',
				value: 'week',
				name: '주차',
			},
			{
				key: 'start',
				value: 'start',
				name: '시작일',
			},
		],
		tableHeader: [
			'구분',
			'이름',
			'휴대폰번호',
			'닉네임',
			'생년월일',
			'상태',
			'주차',
			'교육',
			'환자군',
			'처방코드',
			'시작일',
		],
		deleteTableHeader: ['구분', '이름', '휴대폰번호', '처방코드'],
	},
};

const PATIENT_TYPE = {
	EC: '탐색임상',
	CC: '확증임상',
	PR: '처방',
};

const STATUS = {
	'01': '임신',
	'02': '출산',
};

const PATIENT_GROUP = {
	EXP: '실험군',
	COMP: '대조군',
	TESTER: '테스터',
	COMMON: '일반',
};

const TRAINING = {
	10: '시작 전',
	20: '정상',
	30: '종료',
	40: '미달',
	45: '탈락',
};

function convertDate(YYYYMMDD) {
	return `${YYYYMMDD.slice(0, 4)}-${YYYYMMDD.slice(4, 6)}-${YYYYMMDD.slice(6)}`;
}

function convertPhoneNumber(number) {
	return `${number.slice(0, 3)}-${number.slice(3, -4)}-${number.slice(-4)}`;
}

function convertName(name) {
	if (name.length < 3) {
		return `${name.slice(0, 1)}${'*'.repeat(name.length - 1)}`;
	} else {
		return `${name.slice(0, 1)}${'*'.repeat(name.length - 2)}${name.slice(-1)}`;
	}
}

function MomtheraPatientManageListPage() {
	const navigate = useNavigate();

	const [tableBody, setTableBody] = useState([]);
	const [selectedPatients, setSelectedPatients] = useState([]);
	const [totalDataCnt, setTotalDataCnt] = useState(0);

	const [selectedPage, setSelectedPage] = useState(1);

	const [totalCheck, setTotalCheck] = useState(false);

	const [inputSearchOption, setInputSearchOption] = useState({
		search: '',
		patientType: 'EC|CC|PR',
		patientGroup: 'EXP|COMP|TESTER|COMMON',
		training: '10|20|30|40|45',
	});

	const [searchOption, setSearchOption] = useState({
		search: '',
		patientType: 'EC|CC|PR',
		patientGroup: 'EXP|COMP|TESTER|COMMON',
		training: '10|20|30|40|45',
	});

	const [selectedOrder, setSelectedOrder] = useState('start');
	const [selectedSort, setSelectedSort] = useState('asc');

	// 검색 조건 초기화
	const resetSearchOption = () => {
		setInputSearchOption({
			search: '',
			patientType: 'EC|CC|PR',
			patientGroup: 'EXP|COMP|TESTER|COMMON',
			training: '10|20|30|40|45',
		});
		setSearchOption({
			search: '',
			patientType: 'EC|CC|PR',
			patientGroup: 'EXP|COMP|TESTER|COMMON',
			training: '10|20|30|40|45',
		});
	};

	const applySearchOption = () => {
		setSearchOption(inputSearchOption);
	};

	const goToSelectedPatientDetail = selectedPatient => {
		if (!selectedPatient.pid || selectedPatient.pid === 'Not_linked') {
			alert('해당 환자는 아직 회원가입 하지 않았습니다. 회원가입 완료 후 조회 가능합니다.');
			return;
		}
		navigate(`/dtx006/patient-manage/list/${selectedPatient.pid}`);
	};

	async function getPatientListFromAPI() {
		try {
			const request = {
				...(searchOption.search.trim() && { text: searchOption.search.trim() }),
				...(searchOption.patientType && { 'patient-type': searchOption.patientType }),
				...(searchOption.patientGroup && { 'patient-group': searchOption.patientGroup }),
				...(searchOption.training && { training: searchOption.training }),
				order: selectedOrder,
				sort: selectedSort,
				page: selectedPage,
			};
			const res = await getPatientFromMomthera(request);
			if (parseInt(res.status / 200, 10) === 1) {
				if (res.data.length > 0) {
					const newTableBody = res.data.map(patient => ({
						pid: patient.pid,
						psctId: patient.psctId,
						구분: patient.patientType ? PATIENT_TYPE[patient.patientType] : '-',
						이름: patient.name ? convertName(patient.name) : '-',
						휴대폰번호: patient.phoneNumber ? convertPhoneNumber(patient.phoneNumber) : '-',
						닉네임: patient.nickName || '-',
						생년월일: patient.birth ? convertDate(patient.birth) : '-',
						상태: patient.pregnantStatusCode ? STATUS[patient.pregnantStatusCode] : '-',
						주차: patient.studySession ? `${patient.studySession}주차` : '-',
						교육: patient.training ? TRAINING[patient.training] : '-',
						환자군: patient.patientGroup ? PATIENT_GROUP[patient.patientGroup] : '-',
						처방코드: patient.psctCode || '-',
						시작일: patient.startDate ? convertDate(patient.startDate) : '-',
					}));
					setTableBody(newTableBody);
					setTotalDataCnt(res.data[0].totalCnt);
				} else {
					setTableBody([]);
					setTotalDataCnt(0);
				}
			}
		} catch (err) {
			console.error(err);
			setTableBody([]);
			setTotalDataCnt(0);
		}
	}

	// 신규 환자 추가 modal
	const [showRegisterPatientModal, setShowRegisterPatientModal] = useState(false);

	const [newPatientType, setNewPatientType] = useState('EC');
	const [newPatientGroup, setNewPatientGroup] = useState('');
	const [newPatientDisplayName, setNewPatientDisplayName] = useState('');
	const [newPatientPhone1, setNewPatientPhone1] = useState('010');
	const [newPatientPhone2, setNewPatientPhone2] = useState('');
	const [newPatientPhone3, setNewPatientPhone3] = useState('');
	const [newPatientBirthYear, setNewPatientBirthYear] = useState(1990);
	const [newPatientBirthMonth, setNewPatientBirthMonth] = useState(10);
	const [newPatientBirthDay, setNewPatientBirthDay] = useState(20);
	const [newPatientDoctorCode, setNewPatientDoctorCode] = useState('');

	// 신규 환자 추가 성공 modal
	const [showSuccessRegisterPatient, setShowSuccessRegisterPatient] = useState(false);

	// 신규 환자 추가 실패 (이미 존재하는 환자) modal
	const [showExistPatientModal, setShowExistPatientModal] = useState(false);

	// 환자 삭제 modal
	const [showDeletePatientModal, setShowDeletePatientModal] = useState(false);

	// 환자 삭제 성공 modal
	const [showSuccessDeletePatientModal, setShowSuccessDeletePatientModal] = useState(false);

	const closeRegisterPatientModal = () => {
		setShowRegisterPatientModal(false);
		setNewPatientType('EC');
		setNewPatientGroup('');
		setNewPatientDisplayName('');
		setNewPatientPhone1('010');
		setNewPatientPhone2('');
		setNewPatientPhone3('');
		setNewPatientBirthYear(1990);
		setNewPatientBirthMonth(10);
		setNewPatientBirthDay(20);
		setNewPatientDoctorCode('');
	};

	// 신규 환자 등록하는 function
	async function registerNewPatient() {
		try {
			if (
				newPatientType &&
				newPatientGroup &&
				newPatientDisplayName.trim() !== '' &&
				newPatientPhone1.trim() !== '' &&
				newPatientPhone2.trim() !== '' &&
				newPatientPhone3.trim() !== '' &&
				newPatientDoctorCode.toString().trim() !== ''
			) {
				const request = {
					name: newPatientDisplayName,
					birth:
						newPatientBirthYear.toString() +
						newPatientBirthMonth.toString().padStart(2, 0) +
						newPatientBirthDay.toString().padStart(2, 0),
					phoneNumber: newPatientPhone1 + newPatientPhone2 + newPatientPhone3,
					patientType: newPatientType,
					patientGroup: newPatientGroup,
					psctCode: newPatientDoctorCode,
				};

				const res = await postPatientToMomthera(request);
				if (parseInt(res.status / 200, 10) === 1) {
					if (res.data === 'Duplicate') {
						setShowExistPatientModal(true);
					} else if (res.data === 'FAIL') {
						throw new Error();
					} else {
						setShowSuccessRegisterPatient(true);
					}
				} else {
					throw new Error();
				}
			} else {
				alert('환자 정보를 모두 입력해주세요.');
			}
		} catch (err) {
			console.error(err);
			alert('신규 환자 추가에 실패하였습니다.');
		} finally {
			closeRegisterPatientModal();
		}
	}

	// 처방 코드 생성하는 function
	async function CreatePsctCode() {
		try {
			const res = await getPatientPsctcodeFromMomthera();
			if (parseInt(res.status / 200, 10) === 1) {
				setNewPatientDoctorCode(String(res.data));
			} else {
				alert('의료진 코드 생성에 실패하였습니다.');
			}
		} catch (err) {
			console.error(err);
			alert('의료진 코드 생성에 실패하였습니다.');
		}
	}

	// 신규 환자 modal 생년월일 select option
	const birthYear = () => {
		const yearItems = [];
		for (let year = 1900; year <= new Date().getFullYear(); year++) {
			yearItems.push(
				<option key={year} value={year}>
					{year}
				</option>,
			);
		}
		return yearItems;
	};
	const birthMonth = () => {
		const monthItems = [];
		for (let month = 1; month <= 12; month++) {
			monthItems.push(
				<option key={month} value={month}>
					{month}
				</option>,
			);
		}
		return monthItems;
	};
	const birthDay = () => {
		const dayItems = [];
		const dateNum = new Date(newPatientBirthYear, newPatientBirthMonth, 0).getDate();
		for (let day = 1; day <= dateNum; day++) {
			dayItems.push(
				<option key={day} value={day}>
					{day}
				</option>,
			);
		}
		return dayItems;
	};

	const registerPatientModalBody = (
		<div className="modalbody">
			<div className="formModalItem">
				<div className="formModalItem__label">구분</div>
				<div className="formModalItem__input">
					<select
						className="formModalItem__input__birth"
						onChange={e => {
							if (
								(e.target.selectedOptions[0].value === 'EC' ||
									e.target.selectedOptions[0].value === 'CC') &&
								newPatientGroup === 'COMMON'
							) {
								setNewPatientGroup('');
							} else if (
								e.target.selectedOptions[0].value === 'PR' &&
								(newPatientGroup === 'EXP' || newPatientGroup === 'COMP')
							) {
								setNewPatientGroup('');
							}
							setNewPatientType(e.target.selectedOptions[0].value);
						}}
						value={newPatientType}
					>
						<option key="EC" value="EC">
							탐색임상
						</option>
						<option key="CC" value="CC">
							확증임상
						</option>
						<option key="PR" value="PR">
							처방
						</option>
					</select>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label_large">환자군</div>
				<div className="formModalItem__input">
					<div className="formModalItem__input__radio formModalItem__input__radio_limit_width120">
						<input
							type="radio"
							id="실험군"
							value="EXP"
							name="patient-group"
							checked={newPatientGroup === 'EXP'}
							onChange={e => setNewPatientGroup(e.target.value)}
							disabled={newPatientType === 'PR'}
						/>{' '}
						<label htmlFor="실험군" className="formModalItem__input__radioLabel">
							실험군
						</label>
					</div>
				</div>
				<div className="formModalItem__input">
					<div className="formModalItem__input__radio formModalItem__input__radio_limit_width120">
						<input
							type="radio"
							id="대조군"
							value="COMP"
							name="patient-group"
							checked={newPatientGroup === 'COMP'}
							onChange={e => setNewPatientGroup(e.target.value)}
							disabled={newPatientType === 'PR'}
						/>{' '}
						<label htmlFor="대조군" className="formModalItem__input__radioLabel">
							대조군
						</label>
					</div>
				</div>
				<div className="formModalItem__input">
					<div className="formModalItem__input__radio formModalItem__input__radio_limit_width120">
						<input
							type="radio"
							id="테스터"
							value="TESTER"
							name="patient-type"
							checked={newPatientGroup === 'TESTER'}
							onChange={e => setNewPatientGroup(e.target.value)}
						/>{' '}
						<label htmlFor="테스터" className="formModalItem__input__radioLabel">
							테스터
						</label>
					</div>
				</div>
				<div className="formModalItem__input">
					<div className="formModalItem__input__radio formModalItem__input__radio_limit_width120">
						<input
							type="radio"
							id="일반"
							value="COMMON"
							name="patient-type"
							checked={newPatientGroup === 'COMMON'}
							onChange={e => setNewPatientGroup(e.target.value)}
							disabled={newPatientType === 'EC' || newPatientType === 'CC'}
						/>{' '}
						<label htmlFor="일반" className="formModalItem__input__radioLabel">
							일반
						</label>
					</div>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">이름</div>
				<div className="formModalItem__input">
					<input
						type="text"
						className="formModalItem__input__plainText"
						placeholder="이름"
						value={newPatientDisplayName}
						onChange={e => setNewPatientDisplayName(e.target.value)}
					/>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">휴대폰 번호</div>
				<div className="formModalItem__input">
					<select
						className="formModalItem__input__phoneNumber"
						value={newPatientPhone1}
						onChange={e => {
							setNewPatientPhone1(e.target.value);
						}}
						style={{ width: '5.625rem', height: '2rem' }}
					>
						<option value="010">010</option>
						<option value="011">011</option>
						<option value="016">016</option>
						<option value="017">017</option>
						<option value="018">018</option>
						<option value="019">019</option>
					</select>
					<span className="phoneDivider">-</span>
					<input
						type="text"
						className="formModalItem__input__phoneNumber"
						value={newPatientPhone2}
						onChange={e => {
							setNewPatientPhone2(e.target.value);
						}}
						size={4}
						maxLength={4}
						placeholder="1234"
					/>
					<span className="phoneDivider">-</span>
					<input
						type="text"
						className="formModalItem__input__phoneNumber"
						value={newPatientPhone3}
						onChange={e => {
							setNewPatientPhone3(e.target.value);
						}}
						size={4}
						maxLength={4}
						placeholder="5678"
					/>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">생년월일</div>
				<div className="formModalItem__input">
					<select
						className="formModalItem__input__birth"
						onChange={e => setNewPatientBirthYear(e.target.selectedOptions[0].value)}
						value={newPatientBirthYear}
					>
						{birthYear()}
					</select>
					<span className="birthText">년</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setNewPatientBirthMonth(e.target.selectedOptions[0].value)}
						value={newPatientBirthMonth}
					>
						{birthMonth()}
					</select>
					<span className="birthText">월</span>
					<select
						className="formModalItem__input__birth"
						onChange={e => setNewPatientBirthDay(e.target.selectedOptions[0].value)}
						value={newPatientBirthDay}
					>
						{birthDay()}
					</select>
					<span className="birthText">일</span>
				</div>
			</div>
			<div className="formModalItem">
				<div className="formModalItem__label">의료진 코드</div>
				<div className="formModalItem__input">
					<input
						type="text"
						readOnly
						className="formModalItem__input__plainTextWithButton"
						placeholder="의료진 코드"
						value={newPatientDoctorCode}
						onChange={e => setNewPatientDoctorCode(e.target.value)}
					/>
					<button
						className="submitButton__rounded"
						onClick={() => CreatePsctCode()}
						disabled={
							!(
								newPatientDisplayName &&
								newPatientPhone1 &&
								newPatientPhone2 &&
								newPatientPhone3 &&
								newPatientType &&
								newPatientGroup
							)
						}
					>
						생성
					</button>
				</div>
			</div>
		</div>
	);

	const registerPatientModalFooter = (
		<div className="modalfooter">
			<button
				className="submitButton__rounded"
				onClick={() => registerNewPatient()}
				disabled={newPatientDoctorCode === ''}
			>
				저장
			</button>
			<button className="cancelButton__rounded" onClick={() => closeRegisterPatientModal()}>
				취소
			</button>
		</div>
	);

	// 신규 환자 추가 성공 modal
	const closeSuccessRegisterPatientModal = () => {
		setShowSuccessRegisterPatient(false);
		getPatientListFromAPI();
	};

	const SuccessRegisterPatientModalBody = (
		<div className="modalbody">정상적으로 환자가 등록되었습니다.</div>
	);

	const SuccessRegisterPatientModalFooter = (
		<div className="modalfooter">
			<button
				className="submitButton__rounded"
				onClick={closeSuccessRegisterPatientModal}
				style={{ width: '100%' }}
			>
				확인
			</button>
		</div>
	);

	// 신규 환자 추가 실패 modal
	const closeExistPatientModal = () => {
		setShowExistPatientModal(false);
	};

	const existPatientModalBody = <div className="modalbody">이미 등록된 환자입니다.</div>;

	const existPatientModalFooter = (
		<div className="modalfooter">
			<button
				className="submitButton__rounded"
				onClick={() => closeExistPatientModal()}
				style={{ width: '100%' }}
			>
				확인
			</button>
		</div>
	);

	// 환자 삭제 성공 modal
	const closeSuccessDeletePatientModal = () => {
		setShowSuccessDeletePatientModal(false);
	};

	const SuccessDeletePatientModalBody = <div className="modalbody">삭제되었습니다.</div>;

	const SuccessDeletePatientModalFooter = (
		<div className="modalfooter">
			<button
				className="submitButton__rounded"
				onClick={closeSuccessDeletePatientModal}
				style={{ width: '100%' }}
			>
				확인
			</button>
		</div>
	);

	// 환자 삭제 modal
	const closeDeletePatientModal = () => {
		setShowDeletePatientModal(false);
	};

	async function deleteSelectedPatient() {
		try {
			const request = Object.values(selectedPatients).map(e => Number(e.psctId));
			const res = await deletePatientFromMomthera(request);
			console.log(res, request);
			if (parseInt(res.status / 200, 10) === 1 && res.data === 'OK') {
				setShowSuccessDeletePatientModal(true);
				getPatientListFromAPI();
			} else {
				alert('환자 삭제에 실패하였습니다.');
			}
		} catch (err) {
			console.error(err);
			alert('환자 삭제에 실패하였습니다.');
		} finally {
			closeDeletePatientModal();
			setSelectedPatients([]);
			setTotalCheck(false);
		}
	}

	const deletePatientModalBody = (
		<div className="modalbody">
			<div className="spanWrapContainer">
				<span>해당 환자를 삭제하시겠습니까?</span>
				<table className="AlcoholCommonTable">
					<thead>
						<tr>
							{pageInfo.list.deleteTableHeader.map(headerItem => (
								<th key={headerItem}>{headerItem}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{Object.values(selectedPatients).map((patientInfo, idx) => (
							<tr key={idx}>
								{pageInfo.list.deleteTableHeader.map(headerItem => (
									<td key={headerItem}>{patientInfo[headerItem]}</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);

	const deletePatientModalFooter = (
		<div className="modalfooter">
			<button
				className="submitButton__rounded"
				onClick={() => deleteSelectedPatient()}
				style={{ width: '100% !important' }}
			>
				삭제
			</button>
			<button
				className="cancelButton__rounded"
				onClick={() => closeDeletePatientModal()}
				style={{ width: '100% !important' }}
			>
				취소
			</button>
		</div>
	);

	// pagination
	const getNewTableDataByPagination = page => {
		setSelectedPage(page);
		setSelectedPatients([]);
		setTotalCheck(false);
	};

	useEffect(() => {
		getPatientListFromAPI();
	}, [selectedPage]);

	useEffect(() => {
		getPatientListFromAPI();
		getNewTableDataByPagination(1);
	}, [searchOption, selectedOrder, selectedSort]);

	return (
		<div>
			<SearchTablePageLayout
				type="patient"
				pageInfo={pageInfo}
				tableBody={tableBody}
				tablePageCnt={Math.ceil(totalDataCnt / 20) || 1}
				selectedPage={selectedPage}
				setSelectedPage={setSelectedPage}
				getNewTableDataByPagination={getNewTableDataByPagination}
				setShowRegisterModal={setShowRegisterPatientModal}
				setShowDeleteModal={setShowDeletePatientModal}
				checkedItems={selectedPatients}
				setCheckedItems={setSelectedPatients}
				totalCheck={totalCheck}
				setTotalCheck={setTotalCheck}
				searchOption={inputSearchOption}
				setSearchOption={setInputSearchOption}
				resetSearchOption={resetSearchOption}
				applySearchOption={applySearchOption}
				selectedOrder={selectedOrder}
				setSelectedOrder={setSelectedOrder}
				selectedSort={selectedSort}
				setSelectedSort={setSelectedSort}
				clickEvent={goToSelectedPatientDetail}
			/>
			{/* 신규 추가 modal */}
			<CommonModal
				header="신규환자 추가"
				body={registerPatientModalBody}
				footer={registerPatientModalFooter}
				show={showRegisterPatientModal}
				setClose={closeRegisterPatientModal}
				size="lg"
			/>
			{/* 신규 추가 성공 modal */}
			<CommonModal
				header="신규환자 추가"
				body={SuccessRegisterPatientModalBody}
				footer={SuccessRegisterPatientModalFooter}
				show={showSuccessRegisterPatient}
				setClose={closeSuccessRegisterPatientModal}
				size="sm"
			/>
			{/* 신규 추가 실패 (이미 존재하는 환자) modal */}
			<CommonModal
				header="신규환자 추가"
				body={existPatientModalBody}
				footer={existPatientModalFooter}
				show={showExistPatientModal}
				setClose={closeExistPatientModal}
				size="sm"
			/>
			{/* 환자 삭제 modal */}
			<CommonModal
				header="환자 삭제"
				body={deletePatientModalBody}
				footer={deletePatientModalFooter}
				show={showDeletePatientModal}
				setClose={closeDeletePatientModal}
				size="lg"
			/>
			{/* 환자 삭제 성공 modal */}
			<CommonModal
				header="환자 삭제"
				body={SuccessDeletePatientModalBody}
				footer={SuccessDeletePatientModalFooter}
				show={showSuccessDeletePatientModal}
				setClose={closeSuccessDeletePatientModal}
				size="sm"
			/>
		</div>
	);
}

export default MomtheraPatientManageListPage;
